// SPDX-License-Identifier: MIT
// Applies a parsed GenesisState onto the local desktop stores after a resume clone.
// All side effects go through injected seams so the orchestration is unit-tested without
// Tauri; the real wiring (Plan 2) passes the actual store functions. Idempotent slices
// (version append dedup, env reconstruct) are the seam implementations' responsibility.
import type { ProjectRow, ProjectVersionRow } from '../projects/api';
import type { Session } from './session-policy';
import type { GenesisState, GenesisBackendFile } from './genesis-state';
import { genesisToProjectFields, genesisToVersionRows, genesisToSessions } from './genesis-build';

export interface HydrationStores {
  upsertProjectFields: (localId: string, fields: Partial<ProjectRow>, localDir: string) => Promise<void>;
  appendVersions: (rows: ProjectVersionRow[]) => Promise<void>;
  saveSessions: (dir: string, sessions: Session[]) => Promise<void>;
  setSyncMapEntry: (localId: string, cloudId: string) => Promise<void>;
  writeEnv: (dir: string, backend: GenesisBackendFile) => Promise<void>;
}

export async function hydrateLocalStores(
  state: GenesisState,
  localProjectId: string,
  localDir: string,
  stores: HydrationStores,
): Promise<void> {
  if (state.project) {
    await stores.upsertProjectFields(localProjectId, genesisToProjectFields(state.project), localDir);
  }
  await stores.appendVersions(genesisToVersionRows(state.versions, localProjectId));
  await stores.saveSessions(localDir, genesisToSessions(state.sessions));
  if (state.meta.cloudProjectId) {
    await stores.setSyncMapEntry(localProjectId, state.meta.cloudProjectId);
  }
  if (state.backend) {
    await stores.writeEnv(localDir, state.backend);
  }
}
